export type CertificateType = "A1" | "A3" | "";
export type ValidityType = "1-ano" | "2-anos" | "";
export type MediaType = "instalado" | "sem-midia" | "cartao-sem-leitora" | "token" | "cartao-leitora" | "";
export type PersonType = "PF" | "PJ" | "MEI";

export interface CertificateData {
  description: string;
  title: string;
  whereToUse: string[];
  documents: string[];
  compatibility: string[];
}

export const certificateDataByType: Record<PersonType, Record<"A1" | "A3", CertificateData>> = {
  PF: {
    A1: {
      description:
        "O certificado do tipo A1 é em formato de arquivo eletrônico, ou seja, é instalado no computador do usuário. Esse modelo tem validade de até 1 ano, permite fazer cópias e pode ser usado em mais de um equipamento.",
      title: "Certificado digital A1: você seguro e identificado na internet",
      whereToUse: [
        "• Receita Federal (e-CAC)",
        "• DTE (Declaração de Imposto de Renda)",
        "• INSS",
        "• Bancos públicos e privados, Caixa Econômica, Banco do Brasil, Bradesco, Itaú etc.",
        "• Cartórios",
        "• Plataformas de e-commerce, Mercado Livre, B2W",
        "• Plataformas de emissão de notas fiscais",
        "e outros serviços públicos e privados",
      ],
      documents: [
        "**Emissão online:** CNH — Carteira Nacional de Habilitação emitida após 2017 (obrigatória)",
        "**Emissão presencial:** qualquer documento oficial com foto e fé pública, dentro da validade — RG, CPF, carteira de classe (OAB, CRM, CRC etc.), carteira de trabalho ou passaporte",
      ],
      compatibility: ["• Windows", "• Linux", "• macOS"],
    },
    A3: {
      description:
        "O certificado do tipo A3 é armazenado em uma mídia criptográfica, podendo ser cartão ou token. Ele tem validade de 2 anos e é considerado mais seguro que o A1, porque apenas com mídia e senha é possível acessá-lo.",
      title: "Certificado digital A3: você seguro e identificado na internet",
      whereToUse: [
        "• Receita Federal (e-CAC)",
        "• DTE (Declaração de Imposto de Renda)",
        "• INSS",
        "• Bancos públicos e privados, Caixa Econômica, Banco do Brasil, Bradesco, Itaú etc.",
        "• Cartórios",
        "• Plataformas de emissão de notas fiscais",
        "• Plataformas de e-commerce, Mercado Livre, B2W",
        "• e outros serviços públicos e privados",
      ],
      documents: [
        "**Emissão online:** CNH — Carteira Nacional de Habilitação emitida após 2017 (obrigatória)",
        "**Emissão presencial:** qualquer documento oficial com foto e fé pública, dentro da validade — RG, CPF, carteira de classe (OAB, CRM, CRC etc.), carteira de trabalho ou passaporte",
      ],
      compatibility: ["• Windows", "• Linux", "• macOS"],
    },
  },
  PJ: {
    A1: {
      description:
        "O certificado digital e-CNPJ A1 é em formato de arquivo eletrônico instalado no computador. Tem validade de 1 ano e permite fazer cópias para uso em múltiplos equipamentos.",
      title: "Certificado digital A1: sua empresa segura e identificada na internet",
      whereToUse: [
        "• Receita Federal (e-CAC)",
        "• DTE (Declaração de Imposto de Renda)",
        "• INSS",
        "• Bancos públicos e privados",
        "• Cartórios",
        "• Plataformas de emissão de notas fiscais",
        "• Plataformas de e-commerce",
        "e outros serviços públicos e privados",
      ],
      documents: [
        "**Emissão online:** CNH do responsável legal — Carteira Nacional de Habilitação emitida após 2017 (obrigatória). Demais documentos da empresa são solicitados na videoconferência.",
        "**Emissão presencial:** qualquer documento oficial com foto e fé pública do responsável, dentro da validade (RG, CPF, carteira de classe, carteira de trabalho ou passaporte) + [documento da empresa emitido pela Rede SIM](https://www.gov.br/empresas-e-negocios/pt-br/redesim)",
      ],
      compatibility: ["• Windows", "• Linux", "• macOS"],
    },
    A3: {
      description:
        "O certificado digital e-CNPJ A3 é armazenado em mídia criptográfica (cartão ou token). Tem validade de 2 anos e oferece segurança máxima através de autenticação por mídia física e senha.",
      title: "Certificado digital A3: sua empresa segura e identificada na internet",
      whereToUse: [
        "• Receita Federal (e-CAC)",
        "• DTE (Declaração de Imposto de Renda)",
        "• INSS",
        "• Bancos públicos e privados",
        "• Cartórios",
        "• Plataformas de emissão de notas fiscais",
        "• Plataformas de e-commerce",
        "e outros serviços públicos e privados",
      ],
      documents: [
        "**Emissão online:** CNH do responsável legal — Carteira Nacional de Habilitação emitida após 2017 (obrigatória). Demais documentos da empresa são solicitados na videoconferência.",
        "**Emissão presencial:** qualquer documento oficial com foto e fé pública do responsável, dentro da validade (RG, CPF, carteira de classe, carteira de trabalho ou passaporte) + [documento da empresa emitido pela Rede SIM](https://www.gov.br/empresas-e-negocios/pt-br/redesim)",
      ],
      compatibility: ["• Windows", "• Linux", "• macOS"],
    },
  },
  MEI: {
    A1: {
      description:
        "O certificado digital e-MEI A1 é em formato de arquivo eletrônico instalado no computador. Tem validade de 1 ano e é ideal para microempreendedores individuais.",
      title: "Certificado digital A1: você MEI seguro e identificado na internet",
      whereToUse: [
        "• Portal do Empreendedor",
        "• Receita Federal (e-CAC)",
        "• DASN-SIMEI",
        "• Emissão de NF-e",
        "• Bancos públicos e privados",
        "• Plataformas de e-commerce",
        "e outros serviços públicos e privados",
      ],
      documents: [
        "**Emissão online:** CNH do microempreendedor — Carteira Nacional de Habilitação emitida após 2017 (obrigatória). Documentos adicionais do MEI são solicitados na videoconferência.",
        "**Emissão presencial:** qualquer documento oficial com foto e fé pública do MEI, dentro da validade (RG, CPF, carteira de classe, carteira de trabalho ou passaporte) + CCMEI ou [documento Rede SIM](https://www.gov.br/empresas-e-negocios/pt-br/redesim)",
      ],
      compatibility: ["• Windows", "• Linux", "• macOS"],
    },
    A3: {
      description:
        "O certificado digital e-MEI A3 é armazenado em mídia criptográfica (cartão ou token). Tem validade de 2 anos e oferece máxima segurança para suas operações como MEI.",
      title: "Certificado digital A3: você MEI seguro e identificado na internet",
      whereToUse: [
        "• Portal do Empreendedor",
        "• Receita Federal (e-CAC)",
        "• DASN-SIMEI",
        "• Emissão de NF-e",
        "• Bancos públicos e privados",
        "• Plataformas de e-commerce",
        "e outros serviços públicos e privados",
      ],
      documents: [
        "**Emissão online:** CNH do microempreendedor — Carteira Nacional de Habilitação emitida após 2017 (obrigatória). Documentos adicionais do MEI são solicitados na videoconferência.",
        "**Emissão presencial:** qualquer documento oficial com foto e fé pública do MEI, dentro da validade (RG, CPF, carteira de classe, carteira de trabalho ou passaporte) + CCMEI ou [documento Rede SIM](https://www.gov.br/empresas-e-negocios/pt-br/redesim)",
      ],
      compatibility: ["• Windows", "• Linux", "• macOS"],
    },
  },
};

// Preços base dos certificados
export const getBasePrice = (
  personType: PersonType,
  certType: CertificateType,
  validity: ValidityType
): number => {
  if (!certType) return 0;

  if (personType === "PF") {
    if (certType === "A1") return 186.9;
    if (certType === "A3") return 252.9;
  }

  if (personType === "PJ") {
    if (certType === "A1") return 274.9;
    if (certType === "A3") return 378.9;
  }

  if (personType === "MEI") {
    if (certType === "A1") return 219.9;
    if (certType === "A3") return 298.9;
  }

  return 0;
};

// Preços adicionais por tipo de mídia
export const getMediaAddon = (media: MediaType): number => {
  switch (media) {
    case "instalado":
      return 0;
    case "sem-midia":
      return 0;
    case "cartao-sem-leitora":
      return 0;
    case "token":
      return 224.0;
    case "cartao-leitora":
      return 0;
    default:
      return 0;
  }
};

// Cálculo do preço total
export const getPrice = (
  personType: PersonType,
  certType: CertificateType,
  validity: ValidityType,
  media: MediaType
): number => {
  if (!certType) return 0;

  // A1: validade fixa em 1 ano, sem mídia
  if (certType === "A1") {
    return getBasePrice(personType, certType, "1-ano");
  }

  // A3: validade fixa em 2 anos, requer mídia
  if (!media) return 0;

  return getBasePrice(personType, certType, "2-anos") + getMediaAddon(media);
};

// Formatação de preço em BRL
export const formatBRL = (value: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);

// Labels de mídia
export const getMediaLabel = (media: MediaType): string => {
  const labels: Record<MediaType, string> = {
    instalado: "Instalado no computador",
    "sem-midia": "Sem mídia",
    "cartao-sem-leitora": "Cartão sem leitora",
    token: "Token",
    "cartao-leitora": "Cartão + leitora",
    "": "",
  };
  return labels[media] || "";
};

// Opções de mídia disponíveis para A3
export const mediaOptions: { value: MediaType; label: string }[] = [
  { value: "sem-midia", label: "Sem mídia" },
  { value: "cartao-sem-leitora", label: "Cartão sem leitora" },
  { value: "token", label: "Token" },
  { value: "cartao-leitora", label: "Cartão + leitora" },
];

// A3 tem validade fixa de 2 anos — lista mantida só para compatibilidade com componentes legados
export const validityOptions: { value: ValidityType; label: string }[] = [
  { value: "2-anos", label: "2 anos" },
];
